import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { PASSWORD_MIN } from "../../shared/contracts.js";
import { cx } from "../lib/cx.js";
import { assessPassword } from "../lib/password.js";
import { Input } from "./ui/Form.js";

/** Campo de contraseña con botón para mostrarla (evita errores de tipeo sin pedir repetirla dos veces a ciegas). */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size">>(
  function PasswordInput({ className, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <div className={cx("relative", className)}>
        <Input ref={ref} type={visible ? "text" : "password"} className="pr-10" spellCheck={false} {...rest} />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
          aria-pressed={visible}
          className="absolute top-1/2 right-1 grid size-7 -translate-y-1/2 place-items-center rounded text-subtle hover:bg-surface-2 hover:text-fg"
        >
          {visible ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
        </button>
      </div>
    );
  },
);

const BAR_TONES = ["bg-danger", "bg-danger", "bg-warning", "bg-success", "bg-success"] as const;

/** Medidor de fortaleza: 4 segmentos + etiqueta + sugerencias concretas. */
export function PasswordStrength({ password, email, fullName, id }: { password: string; email?: string; fullName?: string; id?: string }) {
  const a = assessPassword(password, { email, fullName });
  return (
    <div id={id} className="space-y-1.5" aria-live="polite">
      <div className="flex gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cx("h-1.5 flex-1 rounded-full", password && a.score >= i ? BAR_TONES[a.score] : "bg-surface-3")}
          />
        ))}
      </div>
      <p className="text-[12.5px] text-muted">
        <span className="font-medium text-fg">Fortaleza: {a.label}.</span>{" "}
        {password
          ? a.hints.join(" ")
          : `Mínimo ${PASSWORD_MIN} caracteres, sin otras reglas: se recomienda una frase de varias palabras.`}
      </p>
    </div>
  );
}
